import { tagKurz, type ErledigterTag } from '@netcon-work/shared'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { AufgabenVerweis } from '../AufgabeKontext'
import { SectionTitle } from '../ui/primitives'

/** Wie viele Tage offen mit Titeln dastehen. Der Rest liegt hinter einem Klick. */
const OFFENE_TAGE = 3
/** Wie viele Titel je Tag, bevor „+ n weitere" übernimmt. */
const TITEL_JE_TAG = 4
/** Im Modus „Einfach": wie viele Zeilen des jüngsten Tages offen dastehen. */
const ZEILEN_EINFACH = 6

/**
 * **Zone 4 – die Belohnungszone** ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * Vorher stand hier „37 erledigt – zeigen": eine Zahl hinter einem Klick, die niemand drückt.
 * Was man geschafft hat, ist der einzige Teil des Tages, der einen trägt – der gehört sichtbar.
 *
 * Drei Tage offen mit Titeln, darüber der Verlauf der letzten vier Wochen als Streifen,
 * dahinter der Rest. Dieselbe Frist wie im Trichter: lange genug zum Nachsehen, kurz genug
 * zum Vergessen.
 */
export function Geschafft({
  verlauf,
  einfach = false,
}: {
  verlauf: readonly ErledigterTag[]
  /**
   * **Die senkrechte Form für den Modus „Einfach"**
   * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
   *
   * Dieselben Daten, andere Richtung. Nebeneinanderliegende Tagesblöcke beantworten *„wie war
   * die Woche"*; unter „Jetzt" gehört die Antwort auf *„was habe ich gerade weggeschafft"* – und
   * die ist eine Liste, die vom Jetzt weg nach unten in die Vergangenheit läuft.
   */
  einfach?: boolean
}) {
  const [alles, setAlles] = useState(false)

  const gesamt = verlauf.reduce((s, t) => s + t.anzahl, 0)
  if (gesamt === 0) return null

  /** Neueste zuerst – „heute" gehört nach links, nicht ans Ende einer Liste. */
  const rueckwaerts = [...verlauf].reverse()

  if (einfach) {
    return <Senkrecht verlauf={verlauf} rueckwaerts={rueckwaerts} gesamt={gesamt} />
  }
  const offen = rueckwaerts.filter((t) => t.anzahl > 0).slice(0, OFFENE_TAGE)
  const restTage = rueckwaerts.filter((t) => t.anzahl > 0).slice(OFFENE_TAGE)
  const restAnzahl = restTage.reduce((s, t) => s + t.anzahl, 0)
  const inDreiTagen = offen.reduce((s, t) => s + t.anzahl, 0)

  const hoechste = Math.max(...verlauf.map((t) => t.anzahl), 1)

  return (
    <section>
      <SectionTitle hint={`die letzten Tage · ${gesamt} in vier Wochen`}>Geschafft</SectionTitle>

      <div className="rounded-card bg-good-bg px-[15px] py-[13px]">
        <div className="mb-2.5 flex flex-wrap items-center gap-3">
          <b className="tabular text-[13px] text-good-text">
            {inDreiTagen} {inDreiTagen === 1 ? 'Aufgabe' : 'Aufgaben'} an {offen.length}{' '}
            {offen.length === 1 ? 'Tag' : 'Tagen'}
          </b>

          {/* Der Verlauf als Streifen: 28 Balken, der letzte ist heute. Kein Diagramm,
              nur eine Form – man soll sehen, ob es gleichmäßig war, nicht wie viel. */}
          <span
            className="ml-auto flex h-6 items-end gap-[3px]"
            aria-hidden="true"
            title={`Erledigte je Tag, ${verlauf.length} Tage`}
          >
            {verlauf.map((t, i) => (
              <i
                key={t.tag}
                className={`block w-[6px] rounded-sm bg-good ${i === verlauf.length - 1 ? 'opacity-100' : 'opacity-35'}`}
                style={{ height: `${Math.max(8, (t.anzahl / hoechste) * 100)}%` }}
              />
            ))}
          </span>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
          {offen.map((t, i) => (
            <div key={t.tag}>
              <div className="mb-1 text-[10.5px] font-bold tracking-[0.08em] text-good-text uppercase">
                {i === 0 && t.tag === verlauf.at(-1)?.tag ? 'Heute' : tagKurz(t.tag)} ·{' '}
                <span className="tabular">{t.anzahl}</span>
              </div>
              {t.aufgaben.slice(0, TITEL_JE_TAG).map((a) => (
                <AufgabenVerweis
                  key={a.id}
                  aufgabeId={a.id}
                  className="block w-full cursor-pointer truncate py-[2px] text-left text-[12.5px] text-ink2 hover:text-accent hover:underline"
                  title={a.title}
                >
                  <span className="text-good">✓</span> {a.title}
                </AufgabenVerweis>
              ))}
              {t.aufgaben.length > TITEL_JE_TAG && (
                <span className="block py-[2px] text-[12.5px] text-muted">
                  + {t.aufgaben.length - TITEL_JE_TAG} weitere
                </span>
              )}
            </div>
          ))}
        </div>

        {restAnzahl > 0 && (
          <div className="mt-2.5 border-t border-good/20 pt-2">
            <button
              type="button"
              onClick={() => setAlles((a) => !a)}
              className="cursor-pointer text-[12.5px] font-semibold text-good-text hover:underline"
            >
              {alles ? 'wieder zuklappen' : `${restAnzahl} weitere in den letzten vier Wochen`}
            </button>
            {alles && (
              <div className="mt-1.5">
                {restTage.map((t) => (
                  <div key={t.tag} className="flex flex-wrap items-baseline gap-2 py-[3px]">
                    <span className="tabular w-[70px] shrink-0 text-[11.5px] text-muted">
                      {tagKurz(t.tag)}
                    </span>
                    <span className="flex min-w-0 flex-wrap gap-x-3 text-[12.5px] text-ink2">
                      {t.aufgaben.map((a) => (
                        <AufgabenVerweis
                          key={a.id}
                          aufgabeId={a.id}
                          className="cursor-pointer truncate hover:text-accent hover:underline"
                        >
                          {a.title}
                        </AufgabenVerweis>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

/* ── Die senkrechte Form (Modus „Einfach") ──────────────────────────── */

/**
 * **Das Neueste oben, direkt unter „Jetzt"** – das Spiegelbild des Vorlaufs.
 *
 * Nils, 28.08.2026: *„bei «erledigt» genau umgekehrt das neuste oben bei «von jetzt»."*
 *
 * Der Vorlauf lässt das Wichtigste **nach unten** zur Mitte wachsen; hier läuft das Fertige von
 * der Mitte **nach unten** weg und verblasst dabei. Zusammen ergibt das eine Achse, auf der man
 * die Uhrzeit nicht lesen muss, um zu wissen, wo man steht.
 *
 * Offen steht nur der jüngste Tag mit Inhalt. Frühere Tage sind eine Zeile mit Zahl – wer sie
 * aufklappt, will zurücksehen, und das ist eine andere Absicht als der laufende Tag.
 */
function Senkrecht({
  verlauf,
  rueckwaerts,
  gesamt,
}: {
  verlauf: readonly ErledigterTag[]
  rueckwaerts: readonly ErledigterTag[]
  gesamt: number
}) {
  const [zurueck, setZurueck] = useState(false)
  const [mehrHeute, setMehrHeute] = useState(false)

  const mitInhalt = rueckwaerts.filter((t) => t.anzahl > 0)
  const [juengster, ...frueher] = mitInhalt
  if (!juengster) return null

  const heute = juengster.tag === verlauf.at(-1)?.tag
  const zeilen = mehrHeute ? juengster.aufgaben : juengster.aufgaben.slice(0, ZEILEN_EINFACH)
  const restHeute = juengster.aufgaben.length - zeilen.length
  const restTage = frueher.reduce((s, t) => s + t.anzahl, 0)
  const hoechste = Math.max(...verlauf.map((t) => t.anzahl), 1)

  return (
    <section>
      <div className="mb-[9px] flex flex-wrap items-baseline gap-x-[9px] gap-y-1">
        <h3 className="text-[12px] font-bold tracking-[0.07em] uppercase">Erledigt</h3>
        <span className="tabular text-xs text-muted">
          {juengster.anzahl} {heute ? 'heute' : `am ${tagKurz(juengster.tag)}`} · das Neueste oben
        </span>
        {/* Derselbe Streifen wie in der ausführlichen Form: 28 Balken, der letzte ist heute. */}
        <span
          className="ml-auto flex h-4 items-end gap-[3px]"
          aria-hidden="true"
          title={`Erledigte je Tag, ${verlauf.length} Tage · ${gesamt} in vier Wochen`}
        >
          {verlauf.map((t, i) => (
            <i
              key={t.tag}
              className={`block w-[5px] rounded-sm bg-good ${
                i === verlauf.length - 1 ? 'opacity-100' : 'opacity-30'
              }`}
              style={{ height: `${Math.max(8, (t.anzahl / hoechste) * 100)}%` }}
            />
          ))}
        </span>
      </div>

      <div className="overflow-hidden rounded-card border border-line">
        {zeilen.map((a, i) => (
          <div
            key={a.id}
            className={cn(
              'flex flex-wrap items-center gap-x-2.5 px-3.5 py-[7px] not-first:border-t not-first:border-line',
              /**
               * **Das Jüngste trägt die grüne Fläche.** Es ist das, was man gerade getan hat –
               * und der einzige Teil des Tages, der einen trägt. Nach unten verblasst es, weil
               * die Freude über eine Aufgabe von vor drei Stunden auch verblasst ist.
               */
              i === 0
                ? 'bg-good-bg'
                : i === 1
                  ? 'opacity-85'
                  : i === 2
                    ? 'opacity-70'
                    : 'opacity-55',
            )}
          >
            <span className="shrink-0 text-[12px] font-bold text-good">✓</span>
            <AufgabenVerweis
              aufgabeId={a.id}
              title={a.title}
              className="min-w-0 flex-1 cursor-pointer truncate text-left text-[12.5px] hover:text-accent hover:underline"
            >
              {a.title}
            </AufgabenVerweis>
            <span className="tabular shrink-0 text-[11px] text-muted">{uhrzeit(a.am)}</span>
          </div>
        ))}

        {restHeute > 0 && (
          <button
            type="button"
            onClick={() => setMehrHeute(true)}
            className="w-full cursor-pointer border-t border-line px-3.5 py-1.5 text-left text-[11.5px] text-muted hover:text-accent-deep"
          >
            + {restHeute} weitere
          </button>
        )}

        {restTage > 0 && (
          <>
            <button
              type="button"
              onClick={() => setZurueck((z) => !z)}
              aria-expanded={zurueck}
              className="w-full cursor-pointer border-t border-line bg-[#fbfaf8] px-3.5 py-[7px] text-left text-[11px] font-semibold tracking-[0.06em] text-muted uppercase hover:text-accent-deep"
            >
              {zurueck ? '▴ zuklappen' : `▾ davor · ${restTage} in vier Wochen`}
            </button>
            {zurueck &&
              frueher.map((t) => (
                <div key={t.tag} className="border-t border-line px-3.5 py-2">
                  <div className="tabular mb-0.5 text-[10.5px] font-bold tracking-[0.07em] text-muted uppercase">
                    {tagKurz(t.tag)} · {t.anzahl}
                  </div>
                  {t.aufgaben.map((a) => (
                    <AufgabenVerweis
                      key={a.id}
                      aufgabeId={a.id}
                      title={a.title}
                      className="block w-full cursor-pointer truncate py-[1px] text-left text-[12px] text-ink2 hover:text-accent hover:underline"
                    >
                      <span className="text-good">✓</span> {a.title}
                    </AufgabenVerweis>
                  ))}
                </div>
              ))}
          </>
        )}
      </div>
    </section>
  )
}

/** „vor 20 Min." für das eben Fertige, sonst die Uhrzeit. */
function uhrzeit(am: string): string {
  const minuten = Math.floor((Date.now() - new Date(am).getTime()) / 60_000)
  if (minuten < 60) return `vor ${Math.max(1, minuten)} Min.`
  return new Date(am).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}
